import { useState } from 'react';

interface FAQItem {
  q: string;
  a: string;
}

interface FAQSectionProps {
  faqs?: FAQItem[];
  headline?: string;
  subheadline?: string;
}

const defaultFaqs: FAQItem[] = [
  {
    q: 'Who rents studio space near me?',
    a: 'Book My Studio Space offers professional studio rentals across major US cities including New York, Los Angeles, Chicago, and Miami. Our studio locations provide fully equipped spaces for photography, video production, podcasting, and content creation with flexible hourly and daily booking options available.',
  },
  {
    q: 'How much does it cost to rent a studio?',
    a: 'Studio rental pricing starts from around forty-five dollars per hour, with half-day rates from one hundred sixty-five dollars and full-day bookings from two hundred ninety-nine dollars. Pricing varies by studio type, equipment included, and location. All pricing is transparent at checkout with zero hidden fees.',
  },
  {
    q: 'What\'s included in a studio rental?',
    a: 'Every studio rental includes professional-grade equipment relevant to the studio type — lighting rigs and backdrops for photography, acoustic treatment and teleprompters for video, broadcast microphones and mixing consoles for podcasting, and streaming gear for content creation. Full equipment lists are shown when booking.',
  },
  {
    q: 'How long should I book a studio?',
    a: 'Studios can be booked by the hour, half-day, or full day. Quick headshot sessions or individual podcast episodes typically need one to two hours. Brand shoots and multi-scene video productions often require four to eight hours or multiple consecutive days. Book only the time you need — no minimum commitment.',
  },
  {
    q: 'Can I bring my own equipment to the studio?',
    a: 'Absolutely. You are welcome to bring your own cameras, lenses, lighting, microphones, and production equipment. Many creators use our spaces as a base and supplement with their own gear. Studios are designed to accommodate external equipment with power outlets, rigging points, and generous floor space.',
  },
  {
    q: 'How do I book a studio space instantly?',
    a: 'Booking is fast and fully online. Browse available studios by type and city, choose your preferred date and time slot, complete secure checkout, and receive instant confirmation with studio access details. The entire process takes under five minutes and no phone call or approval is required.',
  },
  {
    q: 'Is parking available at studio locations?',
    a: 'Parking availability varies by location. Most studios are located near public transit and many offer dedicated parking or loading access. Full parking and transit details are provided in your confirmation email along with directions and studio access instructions immediately after booking.',
  },
  {
    q: 'Can I cancel or reschedule my studio booking?',
    a: 'Yes. Bookings can be cancelled or rescheduled up to forty-eight hours before your session without penalty. Changes within forty-eight hours are subject to a rescheduling fee. Full cancellation and rescheduling terms are available in our booking policy and shown at checkout before you confirm your reservation.',
  },
];

export default function FAQSection({ faqs = defaultFaqs, headline = 'Frequently Asked Questions', subheadline = 'Everything you need to know about studio rentals, pricing, and how we work.' }: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="py-24 bg-white" id="faq">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-amber-600 text-xs font-bold uppercase tracking-widest mb-3 block">
            Common Questions
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-stone-900 mb-5">{headline}</h2>
          <p className="text-stone-500 text-lg">{subheadline}</p>
        </div>

        {/* Accordion */}
        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className={`border rounded-xl overflow-hidden transition-all duration-200 ${
                openIndex === idx ? 'border-amber-400' : 'border-stone-200'
              }`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer bg-white hover:bg-stone-50 transition-colors"
              >
                <span className="font-semibold text-stone-900 text-base leading-snug pr-4">{faq.q}</span>
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                    openIndex === idx ? 'bg-amber-500 text-white rotate-45' : 'bg-stone-100 text-stone-500'
                  }`}
                >
                  <i className="ri-add-line text-sm" />
                </div>
              </button>
              {openIndex === idx && (
                <div className="px-6 pb-6 pt-1 border-l-4 border-amber-400 bg-amber-50/30">
                  <p className="text-stone-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Still have questions? */}
        <div className="mt-12 text-center bg-stone-50 rounded-2xl p-8 border border-stone-200">
          <h3 className="font-serif text-2xl font-bold text-stone-900 mb-3">Still have questions?</h3>
          <p className="text-stone-500 text-sm mb-6">Our team is happy to help you find the right studio for your project.</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="tel:+18005551234"
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-6 py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line" />
              Call (800) 555-1234
            </a>
            <a
              href="mailto:hello@bookmystudiospace.com"
              className="flex items-center gap-2 border border-stone-300 hover:border-stone-400 text-stone-700 hover:text-stone-900 text-sm font-semibold px-6 py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line" />
              Email Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
